import React, { useState } from 'react';
import { usePWAInstall, usePushNotifications, registerServiceWorker, subscribeToPush } from '@/utils/pwa';
import { useAuth } from '@/App';
import { 
  Download, 
  Bell, 
  BellOff, 
  X, 
  Smartphone,
  Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Check initial dismissed state from localStorage
const getInitialDismissed = () => {
  const wasDismissed = localStorage.getItem('pwa-install-dismissed');
  if (wasDismissed) {
    const dismissedAt = parseInt(wasDismissed);
    // Show again after 7 days
    if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) {
      return true;
    }
  }
  return false;
};

// PWA Install Banner - shows when app can be installed
export const PWAInstallBanner = () => {
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const [dismissed, setDismissed] = useState(getInitialDismissed);
  
  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };
  
  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      setDismissed(true);
    }
  };
  
  if (isInstalled || dismissed || !isInstallable) {
    return null;
  }
  
  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg p-4 z-50 animate-slide-up">
      <button 
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 text-white/70 hover:text-white"
      >
        <X className="w-4 h-4" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="p-2 bg-white/20 rounded-lg dark:bg-zinc-900">
          <Smartphone className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-white text-sm">Install Nerava</h4>
          <p className="text-white/80 text-xs mt-1">
            Akses lebih cepat dari home screen, notifikasi real-time, dan mode offline.
          </p>
          <Button 
            onClick={handleInstall}
            size="sm"
            className="mt-3 bg-white text-indigo-600 hover:bg-white/90 dark:bg-zinc-900 dark:text-indigo-400"
          >
            <Download className="w-4 h-4 mr-1" />
            Install Sekarang
          </Button>
        </div>
      </div>
    </div>
  );
};

// Notification Permission Button
export const NotificationButton = ({ className = '' }) => {
  const { permission, requestAndSubscribe, isSupported } = usePushNotifications();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  
  if (!isSupported) {
    return null;
  }
  
  const handleRequest = async () => {
    setLoading(true);
    await requestAndSubscribe(user?.id, token);
    setLoading(false);
  };
  
  if (permission === 'granted') {
    return (
      <button 
        className={`flex items-center gap-2 px-3 py-2 bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400 rounded-lg text-sm ${className}`}
        disabled
      >
        <Check className="w-4 h-4" />
        Notifikasi Aktif
      </button>
    );
  }
  
  if (permission === 'denied') {
    return (
      <button 
        className={`flex items-center gap-2 px-3 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm ${className}`}
        disabled
      >
        <BellOff className="w-4 h-4" />
        Notifikasi Diblokir
      </button>
    );
  }
  
  return (
    <button 
      onClick={handleRequest}
      disabled={loading}
      className={`flex items-center gap-2 px-3 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-lg text-sm hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors ${className}`}
    >
      <Bell className="w-4 h-4" />
      {loading ? 'Loading...' : 'Aktifkan Notifikasi'}
    </button>
  );
};

// Notification Settings Card for Settings Page
export const NotificationSettingsCard = () => {
  const { permission, requestAndSubscribe, isSupported } = usePushNotifications();
  const { user, token } = useAuth();
  const { isInstalled } = usePWAInstall();
  const [loading, setLoading] = useState(false);
  
  const handleEnable = async () => {
    setLoading(true);
    await requestAndSubscribe(user?.id, token);
    setLoading(false);
  };
  
  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-800">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
          <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-zinc-50">
            Notifikasi Push
          </h3>
          <p className="text-sm text-slate-500 dark:text-zinc-400">
            Terima notifikasi untuk pesan baru
          </p>
        </div>
      </div>
      
      {!isSupported ? (
        <div className="bg-slate-100 dark:bg-zinc-800 rounded-lg p-3">
          <p className="text-sm text-slate-500 dark:text-zinc-400">
            Browser Anda tidak mendukung push notification.
          </p>
        </div>
      ) : permission === 'granted' ? (
        <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-lg p-3 flex items-center gap-2">
          <Check className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <div>
            <p className="text-sm font-medium text-indigo-700 dark:text-indigo-400">
              Notifikasi Aktif
            </p>
            <p className="text-xs text-indigo-600/70 dark:text-indigo-400">
              Anda akan menerima notifikasi untuk pesan baru
            </p>
          </div>
        </div>
      ) : permission === 'denied' ? (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
          <p className="text-sm text-red-700 dark:text-red-400 font-medium">
            Notifikasi Diblokir
          </p>
          <p className="text-xs text-red-600/70 mt-1 dark:text-red-400">
            Buka pengaturan browser untuk mengizinkan notifikasi dari situs ini.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-zinc-400">
            Aktifkan notifikasi untuk mendapatkan pemberitahuan real-time saat ada pesan baru dari customer.
          </p>
          <Button 
            onClick={handleEnable} 
            disabled={loading}
            className="w-full"
          >
            <Bell className="w-4 h-4 mr-2" />
            {loading ? 'Meminta izin...' : 'Aktifkan Notifikasi'}
          </Button>
        </div>
      )}
      
      {!isInstalled && isSupported && (
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-zinc-800">
          <p className="text-xs text-slate-500 flex items-center gap-1 dark:text-zinc-400">
            <Smartphone className="w-3 h-3" />
            Install aplikasi untuk pengalaman terbaik
          </p>
        </div>
      )}
    </div>
  );
};

// Initialize PWA on app load
export const initializePWA = () => {
  // Register service worker
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      registerServiceWorker();
    });
  }
};

export default {
  PWAInstallBanner,
  NotificationButton,
  NotificationSettingsCard,
  initializePWA
};
